import type { Oidc } from 'oidc-spa/core';

import { OIDC_DISCOVERY_TIMEOUT_MS } from './bounded';

/**
 * Dashboard-owned checks on what the #3882 provider returns (FR-005, FR-011; T062, T063).
 *
 * `oidc-spa`'s vendored `oidc-client-ts` only requires an ID token `sub` claim, and it keeps and
 * uses any refresh token the token endpoint returns. The Dashboard therefore:
 *
 * - validates the ID token's issuer, audience/authorized party and time claims through
 *   `createOidc`'s public `decodedIdTokenSchema` hook, which runs on every token set `oidc-spa`
 *   converts (callback, renewal and restored sessions);
 * - checks expiry against the clock only for freshly received tokens (see
 *   {@link assertIdTokenFresh}), because a session restored from storage legitimately carries an
 *   ID token issued earlier;
 * - removes `refresh_token` from token-endpoint responses before `oidc-client-ts` stores the user,
 *   so renewal can only use the silent `prompt=none` attempt (2026-09-30 clarification).
 *
 * The ID token signature is not re-verified in the browser: it is received directly from the token
 * endpoint over TLS (OIDC Core 3.1.3.7 item 6), and the Backend verifies the access token it
 * actually authorizes against the Cognito JWKS on every request (#3884).
 */

/** Allowed clock skew for `iat`/`nbf`/`exp` checks. */
export const ID_TOKEN_CLOCK_SKEW_SECONDS = 300;

/** The discovery fields the Dashboard needs beyond what `oidc-spa` handles itself. */
export interface DiscoveredProviderMetadata {
  readonly issuer: string;
  readonly tokenEndpoint: string;
}

/** Raised when an ID token fails a Dashboard check; the whole callback or renewal is rejected. */
export class IdTokenValidationError extends Error {
  constructor(reason: string) {
    super(`ID token rejected: ${reason}`);
    this.name = 'IdTokenValidationError';
  }
}

export interface IdTokenExpectations {
  readonly issuer: string;
  readonly clientId: string;
  /** Epoch seconds; injectable for tests. */
  readonly nowSeconds?: () => number;
}

const defaultNowSeconds = (): number => Math.floor(Date.now() / 1000);

const isNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

type DecodedIdToken = Oidc.Tokens.DecodedIdToken_OidcCoreSpec;

/** Checks issuer, audience/azp and `iat`/`nbf`/`exp` shape; throws {@link IdTokenValidationError}. */
export function validateIdTokenClaims(decoded: DecodedIdToken, expectations: IdTokenExpectations): DecodedIdToken {
  const now = (expectations.nowSeconds ?? defaultNowSeconds)();
  const claims = decoded as DecodedIdToken & Record<string, unknown>;

  if (claims.iss !== expectations.issuer) {
    throw new IdTokenValidationError('issuer does not match the discovered issuer');
  }

  const audiences = Array.isArray(claims.aud) ? claims.aud : [claims.aud];
  if (!audiences.includes(expectations.clientId)) {
    throw new IdTokenValidationError('audience does not include this client');
  }
  const needsAuthorizedParty = audiences.length > 1 || claims.azp !== undefined;
  if (needsAuthorizedParty && claims.azp !== expectations.clientId) {
    throw new IdTokenValidationError('authorized party does not match this client');
  }

  if (!isNumber(claims.iat) || claims.iat > now + ID_TOKEN_CLOCK_SKEW_SECONDS) {
    throw new IdTokenValidationError('issued-at time is missing or in the future');
  }
  if (claims.nbf !== undefined && (!isNumber(claims.nbf) || claims.nbf > now + ID_TOKEN_CLOCK_SKEW_SECONDS)) {
    throw new IdTokenValidationError('not-before time is in the future');
  }
  if (!isNumber(claims.exp) || claims.exp <= claims.iat) {
    throw new IdTokenValidationError('expiry is missing or not after issued-at');
  }

  return decoded;
}

/** The `decodedIdTokenSchema` value passed to `createOidc`. */
export function createIdTokenSchema(expectations: IdTokenExpectations): {
  parse: (decoded: DecodedIdToken) => DecodedIdToken;
} {
  return { parse: (decoded) => validateIdTokenClaims(decoded, expectations) };
}

/** True when the ID token has not expired; use only for tokens just received from the provider. */
export function isIdTokenFresh(decoded: DecodedIdToken, nowSeconds: () => number = defaultNowSeconds): boolean {
  const exp = (decoded as DecodedIdToken & Record<string, unknown>).exp;
  return isNumber(exp) && exp > nowSeconds() - ID_TOKEN_CLOCK_SKEW_SECONDS;
}

/** Rejects an expired ID token; call only for tokens just received from the provider. */
export function assertIdTokenFresh(decoded: DecodedIdToken, nowSeconds: () => number = defaultNowSeconds): void {
  if (!isIdTokenFresh(decoded, nowSeconds)) {
    throw new IdTokenValidationError('token has expired');
  }
}

interface RawDiscoveryDocument {
  readonly issuer?: unknown;
  readonly token_endpoint?: unknown;
}

/**
 * Fetches the provider's discovery document with an explicit timeout and returns the issuer and
 * token endpoint. `oidc-spa` performs its own discovery; this second read (usually served from the
 * browser cache) exists because `oidc-spa` does not expose the discovered issuer.
 */
export async function fetchDiscoveredProviderMetadata(
  authority: string,
  fetchImpl: typeof fetch = fetch,
): Promise<DiscoveredProviderMetadata> {
  const url = `${authority.replace(/\/+$/, '')}/.well-known/openid-configuration`;
  const response = await fetchImpl(url, { signal: AbortSignal.timeout(OIDC_DISCOVERY_TIMEOUT_MS) });
  if (!response.ok) {
    throw new Error(`Discovery document request failed with status ${response.status}`);
  }
  const document = (await response.json()) as RawDiscoveryDocument;
  if (typeof document.issuer !== 'string' || typeof document.token_endpoint !== 'string') {
    throw new Error('Discovery document is missing issuer or token_endpoint');
  }
  return { issuer: document.issuer, tokenEndpoint: document.token_endpoint };
}

const requestUrl = (input: RequestInfo | URL): string | null => {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.href;
  return typeof Request !== 'undefined' && input instanceof Request ? input.url : null;
};

const parseRequestUrl = (candidate: string): URL | null => {
  try {
    return new URL(candidate, window.location.href);
  } catch {
    return null;
  }
};

const isSameEndpoint = (candidate: string | null, endpoint: URL): boolean => {
  const parsed = candidate === null ? null : parseRequestUrl(candidate);
  return parsed !== null && parsed.origin === endpoint.origin && parsed.pathname === endpoint.pathname;
};

/** Returns the same response without a `refresh_token` field, or the original when it has none. */
async function withoutRefreshToken(response: Response): Promise<Response> {
  const isJson = (response.headers.get('content-type') ?? '').includes('json');
  if (!response.ok || !isJson) {
    return response;
  }
  const body = (await response.clone().json()) as Record<string, unknown>;
  if (!('refresh_token' in body)) {
    return response;
  }
  const rest = { ...body };
  delete rest.refresh_token;
  const headers = new Headers(response.headers);
  headers.delete('content-length');
  return new Response(JSON.stringify(rest), { status: response.status, statusText: response.statusText, headers });
}

let installedTokenEndpoint: string | null = null;

/**
 * Wraps `window.fetch` once so responses from `tokenEndpoint` never reach `oidc-client-ts` with a
 * refresh token. Idempotent per endpoint; other requests pass through untouched.
 */
export function installRefreshTokenStripper(tokenEndpoint: string, target: Window = window): void {
  if (installedTokenEndpoint === tokenEndpoint) {
    return;
  }
  installedTokenEndpoint = tokenEndpoint;
  const endpoint = new URL(tokenEndpoint);
  const originalFetch = target.fetch.bind(target);

  target.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const response = await originalFetch(input, init);
    return isSameEndpoint(requestUrl(input), endpoint) ? withoutRefreshToken(response) : response;
  };
}

/** Test-only reset for {@link installRefreshTokenStripper}'s idempotency guard. */
export function resetRefreshTokenStripperForTesting(): void {
  installedTokenEndpoint = null;
}
